import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { BroadcastFilter } from '../../../models/BroadcastFilter';
import { IntegrationAutomation, IntegrationSource } from '../../../models/IntegrationSource';
import { IntegrationBroadcastsService } from '../../../shared/services/integration-broadcasts.service';
import { formatDateParam, isInvalidPeriod } from '../date-param';

/**
 * Uma automação da lista, já com os parâmetros do drill-down prontos.
 *
 * `sendsParams` carrega a automação E o período da busca atual: os totais da
 * linha foram calculados dentro daquela janela, então o nível 3 tem que abrir
 * com o mesmo recorte — senão os números não batem com a linha clicada.
 */
type AutomationRow = IntegrationAutomation & {
  sendsParams: { automation: string; start_date?: string; end_date?: string };
};

/**
 * "SMS via Integração", level 2 — the automations of one integration.
 *
 * Each row is an automation configured on the platform side. There is no single
 * "message template" here on purpose: one ActiveCampaign automation can hold
 * several SMS steps with different texts, so the row reports how many distinct
 * messages it fired and the texts themselves live in the sends screen.
 *
 * Drill-down is /integration-sms/view/:source/sends?automation=...
 */
@Component({
  selector: 'app-view-integration-sms',
  templateUrl: './view-integration-sms.component.html',
  styleUrls: ['./view-integration-sms.component.scss']
})
export class ViewIntegrationSmsComponent implements OnInit, OnDestroy {
  source: string;
  integration: IntegrationSource;
  automations: AutomationRow[] = [];
  loading: boolean;

  /** Free-text filter on the automation name. */
  text = '';
  startedMonth: Date;
  endedMonth: Date;

  subscriptions: Subscription[] = [];

  constructor(
    private integrationBroadcastsService: IntegrationBroadcastsService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.route.params.subscribe(params => {
        this.source = params.source;
        this.load();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get invalidPeriod(): boolean {
    return isInvalidPeriod(this.startedMonth, this.endedMonth);
  }

  load(): void {
    // Guarda além do `[disabled]` do botão: o form também é submetido pelo Enter
    // dentro de um campo, que não passa pelo botão.
    if (this.invalidPeriod) {
      return;
    }

    const filter: BroadcastFilter = {
      startDate: formatDateParam(this.startedMonth),
      endDate: formatDateParam(this.endedMonth),
      text: this.text,
      order: null,
      orderDirection: null,
      author: null
    };

    this.loading = true;

    this.subscriptions.push(
      this.integrationBroadcastsService.listAutomations(this.source, filter)
        .pipe(finalize(() => this.loading = false))
        .subscribe(response => {
          const period = this.buildPeriodParams();

          this.integration = response.integration;
          // Os parâmetros do drill-down são calculados AQUI, uma vez por
          // resposta, e não num getter chamado pelo template: o `routerLink` da
          // lupa receberia um objeto novo a cada ciclo de change detection.
          this.automations = response.automations.map(automation => ({
            ...automation,
            sendsParams: { automation: automation.automation ?? '', ...period }
          }));
        })
    );
  }

  /**
   * Opens the sends screen for one automation.
   *
   * The name goes as a query param (it is free text typed on the platform side,
   * so it can contain slashes) and an undeclared automation travels as ''.
   *
   * O PERÍODO vai junto: os totais do card foram calculados dentro da janela que
   * o operador escolheu, então entrar num card e ver a lista de OUTRO período
   * (o padrão, sem filtro) faz os números não baterem com a linha que ele acabou
   * de clicar — e ele não tem como saber que a tela trocou o recorte.
   */
  openSends(automation: AutomationRow): void {
    this.router.navigate(['/integration-sms/view', this.source, 'sends'], {
      queryParams: automation.sendsParams
    });
  }

  /**
   * Período atual no formato da URL do nível 3, omitindo o que não foi
   * preenchido (um `start_date=null` na URL viraria filtro literal).
   */
  private buildPeriodParams(): { start_date?: string; end_date?: string } {
    const params: { start_date?: string; end_date?: string } = {};
    const start = formatDateParam(this.startedMonth);
    const end = formatDateParam(this.endedMonth);

    if (start) {
      params.start_date = start;
    }

    if (end) {
      params.end_date = end;
    }

    return params;
  }
}
